import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarDaysIcon,
  ClockIcon,
  UserIcon,
  PlusIcon,
  FunnelIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const TaskCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // Task data with dates
  const tasks = [
    {
      id: 1,
      title: "Complete Annual Health Assessment",
      assignedBy: "Dr. Meera Gupta",
      dueDate: "2025-07-25",
      startTime: "09:00",
      endTime: "13:00",
      priority: "High",
      status: "In Progress",
      project: "Healthcare Compliance",
      progress: 65,
      category: "Health & Safety",
    },
    {
      id: 2,
      title: "Equipment Maintenance Checklist",
      assignedBy: "Facilities Manager",
      dueDate: "2025-07-26",
      startTime: "14:00",
      endTime: "16:30",
      priority: "Medium",
      status: "In Progress",
      project: "Facilities Management",
      progress: 40,
      category: "Maintenance",
    },
    {
      id: 3,
      title: "Patient Satisfaction Survey Analysis",
      assignedBy: "Quality Director",
      dueDate: "2025-07-28",
      startTime: "10:00",
      endTime: "16:00",
      priority: "Low",
      status: "Completed",
      project: "Patient Experience",
      progress: 100,
      category: "Analysis",
    },
    {
      id: 4,
      title: "Update Emergency Response Procedures",
      assignedBy: "Safety Officer",
      dueDate: "2025-07-29",
      startTime: "08:30",
      endTime: "12:00",
      priority: "High",
      status: "In Progress",
      project: "Safety Compliance",
      progress: 75,
      category: "Safety",
    },
    {
      id: 5,
      title: "Review New Medical Protocol Guidelines",
      assignedBy: "Medical Director",
      dueDate: "2025-07-30",
      startTime: "11:00",
      endTime: "13:00",
      priority: "Medium",
      status: "Pending",
      project: "Protocol Updates",
      progress: 0,
      category: "Training",
    },
    {
      id: 6,
      title: "Submit Monthly Quality Metrics",
      assignedBy: "Quality Assurance Team",
      dueDate: "2025-08-01",
      startTime: "09:00",
      endTime: "12:00",
      priority: "High",
      status: "Not Started",
      project: "Quality Management",
      progress: 0,
      category: "Reporting",
    },
    {
      id: 7,
      title: "Staff Training Session Preparation",
      assignedBy: "Training Coordinator",
      dueDate: "2025-08-05",
      startTime: "13:00",
      endTime: "17:00",
      priority: "Medium",
      status: "Pending",
      project: "Staff Development",
      progress: 15,
      category: "Training",
    },
    {
      id: 8,
      title: "Inventory Management System Update",
      assignedBy: "Inventory Manager",
      dueDate: "2025-08-10",
      startTime: "10:00",
      endTime: "15:00",
      priority: "Low",
      status: "Not Started",
      project: "Supply Chain",
      progress: 0,
      category: "Administration",
    },
  ];

  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    const matchesPriority = selectedPriority === 'all' || task.priority.toLowerCase() === selectedPriority;
    const matchesStatus = selectedStatus === 'all' || task.status.toLowerCase().replace(' ', '') === selectedStatus;
    return matchesPriority && matchesStatus;
  });

  // Helper functions
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800 border-l-4 border-red-500';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-l-4 border-yellow-500';
      case 'Low': return 'bg-green-100 text-green-800 border-l-4 border-green-500';
      default: return 'bg-gray-100 text-gray-800 border-l-4 border-gray-500';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-50 text-green-700';
      case 'In Progress': return 'bg-blue-50 text-blue-700';
      case 'Pending': return 'bg-yellow-50 text-yellow-700';
      case 'Not Started': return 'bg-gray-50 text-gray-700';
      default: return 'bg-gray-50 text-gray-700';
    }
  };

  // Calendar helper functions
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getTasksForDate = (date) => {
    if (!date) return [];
    const dateString = date.toISOString().split('T')[0];
    return filteredTasks.filter(task => task.dueDate === dateString);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}:00`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date) => {
    if (!date) return false;
    return date.getMonth() === currentDate.getMonth();
  };

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      {/* Page Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-4 sm:py-6 space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Task Calendar</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">
                View tasks in calendar format to manage your schedule
              </p>
            </div>
            <Link
              to="/tasks/new"
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
            >
              <PlusIcon className="h-4 w-4" />
              <span>New Task</span>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Calendar Controls */}
        <div className="bg-white rounded-lg shadow-sm border mb-4 sm:mb-6">
          <div className="p-4 sm:p-6 border-b">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              {/* Calendar Navigation */}
              <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                <div className="flex items-center justify-center space-x-2">
                  <button
                    onClick={() => navigateMonth(-1)}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900 min-w-[200px] text-center">
                    {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h2>
                  <button
                    onClick={() => navigateMonth(1)}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </div>
                
                <button
                  onClick={() => setCurrentDate(new Date())}
                  className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                >
                  Today
                </button>
              </div>

              {/* Filters */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                <div className="relative">
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-auto"
                  >
                    <option value="all">All Status</option>
                    <option value="inprogress">In Progress</option>
                    <option value="pending">Pending</option>
                    <option value="notstarted">Not Started</option>
                    <option value="completed">Completed</option>
                  </select>
                  <FunnelIcon className="h-4 w-4 absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>

                <div className="relative">
                  <select
                    value={selectedPriority}
                    onChange={(e) => setSelectedPriority(e.target.value)}
                    className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-auto"
                  >
                    <option value="all">All Priorities</option>
                    <option value="high">High Priority</option>
                    <option value="medium">Medium Priority</option>
                    <option value="low">Low Priority</option>
                  </select>
                  <FunnelIcon className="h-4 w-4 absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>

          {/* Calendar Legend */}
          <div className="px-4 sm:px-6 py-3 bg-gray-50 border-b">
            <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-xs sm:text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-gray-600">High Priority</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="text-gray-600">Medium Priority</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-gray-600">Low Priority</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-gray-600">Today</span>
              </div>
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="bg-white rounded-lg shadow-sm border">
          {/* Calendar Header */}
          <div className="grid grid-cols-7 border-b">
            {dayNames.map((day) => (
              <div key={day} className="p-4 text-center font-medium text-gray-900 bg-gray-50 border-r last:border-r-0">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Body */}
          <div className="grid grid-cols-7">
            {getDaysInMonth(currentDate).map((date, index) => (
              <div
                key={index}
                className={`min-h-[120px] border-r border-b last:border-r-0 ${
                  !date ? 'bg-gray-50' : 
                  !isCurrentMonth(date) ? 'bg-gray-50' :
                  isToday(date) ? 'bg-blue-50' : 'bg-white'
                }`}
              >
                {date && (
                  <>
                    {/* Date Number */}
                    <div className="p-2">
                      <div className={`text-sm font-medium ${
                        isToday(date) ? 'text-blue-600' :
                        isCurrentMonth(date) ? 'text-gray-900' : 'text-gray-400'
                      }`}>
                        {date.getDate()}
                      </div>
                    </div>

                    {/* Tasks for this date */}
                    <div className="px-2 pb-2 space-y-1">
                      {getTasksForDate(date).map((task) => (
                        <Link
                          key={task.id}
                          to={`/tasks/${task.id}`}
                          className={`block p-2 rounded text-xs ${getPriorityColor(task.priority)} hover:shadow-sm transition-shadow`}
                        >
                          <div className="font-medium truncate mb-1">{task.title}</div>
                          <div className="flex items-center text-xs opacity-75">
                            <ClockIcon className="h-3 w-3 mr-1" />
                            <span>{formatTime(task.startTime)} - {formatTime(task.endTime)}</span>
                          </div>
                          <div className="flex items-center text-xs opacity-75 mt-1">
                            <UserIcon className="h-3 w-3 mr-1" />
                            <span className="truncate">{task.assignedBy}</span>
                          </div>
                        </Link>
                      ))}
                      
                      {/* Show count if more than 2 tasks */}
                      {getTasksForDate(date).length > 2 && (
                        <div className="text-xs text-center text-gray-500 py-1">
                          +{getTasksForDate(date).length - 2} more
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Today's Tasks Summary */}
        <div className="mt-6 bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Today's Tasks</h3>
              <CalendarDaysIcon className="h-5 w-5 text-gray-400" />
            </div>
          </div>
          
          <div className="p-6">
            {getTasksForDate(new Date()).length === 0 ? (
              <div className="text-center py-8">
                <CalendarDaysIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No tasks scheduled for today</p>
                <Link
                  to="/tasks/new"
                  className="mt-2 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Schedule New Task
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {getTasksForDate(new Date()).map((task) => (
                  <div key={task.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <Link
                          to={`/tasks/${task.id}`}
                          className="font-medium text-gray-900 hover:text-blue-600 transition-colors"
                        >
                          {task.title}
                        </Link>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(task.status)}`}>
                          {task.status}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          task.priority === 'High' ? 'bg-red-100 text-red-800' :
                          task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {task.priority}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          <span>{formatTime(task.startTime)} - {formatTime(task.endTime)}</span>
                        </div>
                        <div className="flex items-center">
                          <UserIcon className="h-4 w-4 mr-1" />
                          <span>{task.assignedBy}</span>
                        </div>
                        <div>
                          <span className="text-blue-600">{task.project}</span>
                        </div>
                      </div>
                      
                      {task.progress > 0 && (
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                            <span>Progress</span>
                            <span>{task.progress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                              style={{ width: `${task.progress}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="ml-4">
                      <Link
                        to={`/tasks/${task.id}`}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default TaskCalendar;
